import React, { useEffect, useState, useMemo, useCallback } from "react";
import axiosInstanceExternal from "../../utils/axiosInstanceExternal";
import ReactPaginate from "react-paginate";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import Fuse from "fuse.js";
import Select from "react-select";

// Set app element for ReactModal for accessibility
ReactModal.setAppElement("#root"); // Replace '#root' with your actual root element ID

const ContactManagement = () => {
    // --- State Management ---
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);

    // Three-state sort: null = no sort, then asc/desc
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    const [statusFilter, setStatusFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Modals & form data
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    const [contactToDelete, setContactToDelete] = useState(null);
    const [contactToUpdate, setContactToUpdate] = useState({});
    const [contactDetails, setContactDetails] = useState({});
    const [newContact, setNewContact] = useState({
        contact_name: "",
        company_name: "",
        position: "",
        email: "",
        business_email: "",
        phone: "",
        profile_url: "",
        follower_count: "",
        company_website: "",
        company_linkedin: "",
        sales_nav_link: "",
        status: "",
    });

    const [emailBtnDisabled, setEmailBtnDisabled] = useState(false);
    const [addToQueueBtnDisabled, setAddToQueueBtnDisabled] = useState(false);
    const [addToLinkedinExpandDisabled, setAddToLinkedinExpandDisabled] = useState(false);

    const [isRefreshing, setIsRefreshing] = useState(false);

    const [aiPositionFilter, setAiPositionFilter] = useState({ op: "eq", values: [] });

    const contactsPerPage = 30;
    const userEmail = localStorage.getItem("email");

    // --- Advanced filter helpers for AI Position ---
    const OP_OPTIONS = [
        { value: "eq", label: "Equals" },
        { value: "ne", label: "Does not equal" },
        { value: "has", label: "Contains data" },
        { value: "not_has", label: "Does not contain data" },
        { value: "contains", label: "Contains" },
        { value: "not_contains", label: "Does not contain" },
        { value: "begins", label: "Begins with" },
        { value: "not_begins", label: "Does not begin with" },
        { value: "ends", label: "Ends with" },
        { value: "not_ends", label: "Does not end with" },
    ];

    const isNoop = (f) => !f || (f.op === "eq" && (!f.values || f.values.length === 0));

    const hasFilters = searchTerm.trim() !== "" || statusFilter !== "" || !isNoop(aiPositionFilter);

    const matchOne = (raw, f) => {
        const s = (raw ?? "").toString().trim().toLowerCase();
        const vals = (f.values || []).map((v) => v.value.toLowerCase());
        switch (f.op) {
            case "has":
                return s.length > 0;
            case "not_has":
                return s.length === 0;
            case "eq":
                return vals.length === 0 ? true : vals.includes(s);
            case "ne":
                return vals.length === 0 ? true : !vals.includes(s);
            case "contains":
                return vals.length === 0 ? true : vals.some((v) => s.includes(v));
            case "not_contains":
                return vals.length === 0 ? true : vals.every((v) => !s.includes(v));
            case "begins":
                return vals.length === 0 ? true : vals.some((v) => s.startsWith(v));
            case "not_begins":
                return vals.length === 0 ? true : vals.every((v) => !s.startsWith(v));
            case "ends":
                return vals.length === 0 ? true : vals.some((v) => s.endsWith(v));
            case "not_ends":
                return vals.length === 0 ? true : vals.every((v) => !s.endsWith(v));
            default:
                return true;
        }
    };

    // --- Utils ---
    const formatDate = useCallback((dateString) => {
        if (!dateString) return "";
        const dt = new Date(dateString.replace(/ GMT$/, ""));
        const day = String(dt.getDate()).padStart(2, "0");
        const mon = dt.toLocaleString("en-AU", { month: "short" });
        const yr = dt.getFullYear();
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        return `${day} ${mon}, ${yr} ${hh}:${mm}`;
    }, []);

    // --- Data Fetching ---
    const fetchContacts = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const res = await axiosInstanceExternal.get("/contact/contacts");
            setContacts(res.data);
        } catch (err) {
            console.error("Failed to fetch contacts:", err);
            toast.error("Error fetching contacts.");
        } finally {
            setIsRefreshing(false); // ← re-enable
        }
    }, []);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    // --- Fuse for Search ---
    const fuse = useMemo(
        () =>
            new Fuse(contacts, {
                keys: ["contact_name", "company_name", "position"],
                threshold: 0.1,
                ignoreLocation: true,
            }),
        [contacts]
    );

    const statusOptions = useMemo(() => {
        const set = new Set(contacts.map((c) => c.status).filter(Boolean));
        set.add("requested"); // ensure filter always includes "requested"
        return Array.from(set);
    }, [contacts]);

    const aiPositionOptions = useMemo(() => {
        const uniq = Array.from(
            new Set(
                contacts.map((c) => {
                    const v = c.ai_position && String(c.ai_position).trim();
                    return v && v.length ? v : "N/A";
                })
            )
        );
        return uniq.map((v) => ({ value: v, label: v }));
    }, [contacts]);

    // --- Apply Search / Filter / Sort ---
    useEffect(() => {
        let temp = searchTerm ? fuse.search(searchTerm).map((r) => r.item) : [...contacts];

        if (statusFilter) {
            temp = temp.filter((c) => c.status === statusFilter);
        }

        temp = temp.filter((c) =>
            matchOne((c.ai_position && String(c.ai_position).trim()) ? c.ai_position : "N/A", aiPositionFilter)
        );

        if (sortField) {
            temp.sort((a, b) => {
                let aVal = a[sortField];
                let bVal = b[sortField];

                if (sortField === "created_at") {
                    aVal = new Date(aVal?.replace(/ GMT$/, "") || 0);
                    bVal = new Date(bVal?.replace(/ GMT$/, "") || 0);
                } else if (sortField === "follower_count") {
                    const toNum = (v) => Number(String(v ?? "").replace(/[,\s]/g, "")) || 0;
                    aVal = toNum(aVal);
                    bVal = toNum(bVal);
                } else {
                    aVal = aVal?.toString().toLowerCase() || "";
                    bVal = bVal?.toString().toLowerCase() || "";
                }

                if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
                if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
                return 0;
            });
        }

        setFilteredContacts(temp);
    }, [contacts, searchTerm, statusFilter, aiPositionFilter, sortField, sortOrder, fuse]);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, sortField, sortOrder, statusFilter, aiPositionFilter]);

    // --- Pagination ---
    const pageCount = Math.ceil(filteredContacts.length / contactsPerPage);
    const displayedContacts = useMemo(() => {
        const start = currentPage * contactsPerPage;
        return filteredContacts.slice(start, start + contactsPerPage);
    }, [filteredContacts, currentPage]);

    // --- Sort Handlers ---
    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else if (sortOrder === "asc") {
            setSortOrder("desc");
        } else {
            setSortField(null);
            setSortOrder("asc");
        }
    };
    const sortIndicator = (field) => (sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : "");

    // --- CRUD ---
    const handleCreateContact = async () => {
        try {
            const res = await axiosInstanceExternal.post("/contact/contacts", newContact);
            toast.success("Contact created successfully!");
            setContacts([res.data, ...contacts]);
            setIsCreateModalOpen(false);
            setNewContact({
                contact_name: "",
                company_name: "",
                position: "",
                email: "",
                business_email: "",
                phone: "",
                profile_url: "",
                follower_count: "",
                company_website: "",
                company_linkedin: "",
                sales_nav_link: "",
                status: "",
            });
        } catch (err) {
            toast.error(err.response?.data?.error || "Create failed.");
        }
    };

    const handleUpdateContact = async () => {
        try {
            const res = await axiosInstanceExternal.put(`/contact/contacts/${contactToUpdate.contact_id}`, contactToUpdate);
            toast.success("Contact updated successfully!");
            setContacts(contacts.map((c) => (c.contact_id === res.data.contact_id ? res.data : c)));
            setIsUpdateModalOpen(false);
        } catch (err) {
            toast.error("Update failed.");
        }
    };

    const handleDeleteContact = async () => {
        try {
            await axiosInstanceExternal.delete(`/contact/contacts/${contactToDelete.contact_id}`);
            toast.success("Contact deleted successfully!");
            setContacts(contacts.filter((c) => c.contact_id !== contactToDelete.contact_id));
            setIsDeleteModalOpen(false);
            setContactToDelete(null);
        } catch (err) {
            toast.error("Delete failed.");
        }
    };

    const handleInputChange = (e, updateMode = false) => {
        const { name, value } = e.target;
        if (updateMode) {
            setContactToUpdate({ ...contactToUpdate, [name]: value });
        } else {
            setNewContact({ ...newContact, [name]: value });
        }
    };

    // --- Bulk Actions ---
    const handleAddToQueue = async () => {
        const unconnected = filteredContacts.filter((c) => c.status === "unconnected");
        const unconnectedIds = unconnected.map((c) => c.contact_id);
        const profileLinks = unconnected
            .map((c) => (c.profile_url ? String(c.profile_url).trim() : ""))
            .filter(Boolean);

        if (!unconnectedIds.length) {
            toast.info("No unconnected contacts to add to queue.");
            return;
        }

        // keep de-dup local to avoid duplicate API posts
        const links = [...new Set(profileLinks)];

        setAddToQueueBtnDisabled(true);
        try {
            // 1) Existing queue add
            // const { data } = await axiosInstanceExternal.post("/queue/bulkContactToQueue", { contact_ids: unconnectedIds });
            // toast.success(data.message || "Added to queue.");

            // 2) Send profile links to campaign (handles 1 or many)
            if (links.length) {
                const { data } = await axiosInstanceExternal.post("/webhook/expandi-campaign-assign", { profile_links: links });
                const okLinks = (data?.results || []).filter((r) => r.ok).map((r) => r.profile_link);

                // optimistic UI mark
                if (okLinks.length) {
                    setContacts((prev) =>
                        prev.map((c) =>
                            okLinks.includes(String(c.profile_url || "").trim()) ? { ...c, status: "requested" } : c
                        )
                    );
                }

                toast.success(`Sent ${okLinks.length}/${links.length} to campaign.`);
            } else {
                toast.info("No profile links available to send to campaign.");
            }

            await fetchContacts();
        } catch (err) {
            toast.error("Queue or campaign send failed.");
        } finally {
            setAddToQueueBtnDisabled(false);
        }
    };

    const handleAddToLinkedinExpand = async () => {
        if (!filteredContacts.length) {
            toast.info("No contacts to add to LinkedIn Expand.");
            return;
        }
        setAddToLinkedinExpandDisabled(true);
        try {
            const { data } = await axiosInstanceExternal.post("/contact/bulkContactToLinkedinExpand", {
                contact_ids: filteredContacts.map((c) => c.contact_id),
                to_address: userEmail,
            });
            toast.success(data.message || "Added to LinkedIn Expand.");
        } catch (err) {
            toast.error("LinkedIn Expand failed.");
        } finally {
            setAddToLinkedinExpandDisabled(false);
        }
    };

    // single-row actions
    const handleAddSingleToQueue = async (contactId, contactStatus) => {
        if (contactStatus !== "unconnected") {
            toast.info("Only unconnected contacts can be added to the connect queue.");
            return;
        }
        setAddToQueueBtnDisabled(true);
        try {
            // find the contact to extract its profile link
            const contact = contacts.find((c) => c.contact_id === contactId);
            const profileLink = contact?.profile_url && String(contact.profile_url).trim();

            // 1) add to existing connect queue
            // const { data } = await axiosInstanceExternal.post("/queue/bulkContactToQueue", {
            //     contact_ids: [contactId],
            // });
            // toast.success(data.message || "Contact added to queue.");

            // 2) send to campaign if we have a profile link
            if (profileLink) {
                await axiosInstanceExternal.post("/webhook/expandi-campaign-assign", { profile_link: profileLink });
                toast.success("Sent to campaign.");
            } else {
                toast.info("No profile link to send to campaign.");
            }
            // sync UI status locally, then refresh from server
            setContacts((prev) => prev.map((c) => (c.contact_id === contactId ? { ...c, status: "requested" } : c)));
            await fetchContacts();
        } catch (err) {
            toast.error("Queue or campaign send failed.");
        } finally {
            setAddToQueueBtnDisabled(false);
        }
    };

    // single-row actions
    const handleAddSingleToLinkedinExpand = async (contactId) => {
        setAddToLinkedinExpandDisabled(true);
        try {
            const { data } = await axiosInstanceExternal.post("/contact/bulkContactToLinkedinExpand", {
                contact_ids: [contactId],
                to_address: userEmail,
            });
            toast.success(data.message || "Added to LinkedIn Expand.");
        } catch (err) {
            toast.error("LinkedIn Expand failed.");
        } finally {
            setAddToLinkedinExpandDisabled(false);
        }
    };

    const handleEmailData = async () => {
        if (!filteredContacts.length) {
            toast.info("No contacts to email.");
            return;
        }
        setEmailBtnDisabled(true);
        try {
            await axiosInstanceExternal.post("/contact/send_email", {
                email: userEmail,
                contacts: filteredContacts.map((c) => ({ ...c, created_at: formatDate(c.created_at) })),
            });
            toast.success("Email sent!");
        } catch (err) {
            toast.error("Email failed.");
        } finally {
            setEmailBtnDisabled(false);
        }
    };

    // --- Fields Renderer ---
    const contactFields = useMemo(
        () => [
            { label: "Contact Name", name: "contact_name", type: "text" },
            { label: "Company Name", name: "company_name", type: "text" },
            { label: "Position", name: "position", type: "text" },
            { label: "AI Position", name: "ai_position", type: "text" },
            { label: "LinkedIn Email", name: "email", type: "email" },
            { label: "Business Email", name: "business_email", type: "email" },
            { label: "Business Email Status", name: "business_email_status", type: "text" },
            { label: "Phone", name: "phone", type: "text" },
            { label: "Profile URL", name: "profile_url", type: "url", isLink: true },
            { label: "Follower Count", name: "follower_count", type: "text" },
            { label: "Company Website", name: "company_website", type: "url", isLink: true },
            { label: "Company LinkedIn", name: "company_linkedin", type: "url", isLink: true },
            { label: "Sales Nav Link", name: "sales_nav_link", type: "url", isLink: true },
            { label: "Status", name: "status", type: "select", options: statusOptions },
            { label: "Created At", name: "created_at", type: "text", readOnly: true, formatter: formatDate },
        ],
        [statusOptions, formatDate]
    );

    function renderFields(record, updateMode = false, readOnly = false) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contactFields.map((field) => {
                    const value = record[field.name] ?? "";

                    // Link in read-only
                    if (readOnly && field.isLink && value) {
                        const href = value.startsWith("http") ? value : `https://${value}`;
                        return (
                            <div key={field.name} className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                >
                                    {value}
                                </a>
                            </div>
                        );
                    }

                    // Status select
                    if (field.name === "status" && !readOnly) {
                        return (
                            <div key={field.name} className="sm:col-span-1">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <select
                                    name={field.name}
                                    value={value}
                                    onChange={(e) => handleInputChange(e, updateMode)}
                                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-slate-400/40"
                                >
                                    {field.options.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        );
                    }

                    // Default input
                    return (
                        <div key={field.name} className={field.name === "profile_url" ? "sm:col-span-2" : ""}>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                            <input
                                name={field.name}
                                type={field.type || "text"}
                                value={field.formatter ? field.formatter(value) : value}
                                onChange={(e) => !readOnly && handleInputChange(e, updateMode)}
                                readOnly={readOnly || field.readOnly}
                                className={[
                                    "h-11 w-full rounded-xl border px-3 text-sm text-slate-900 transition",
                                    readOnly || field.readOnly
                                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        : "border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300",
                                ].join(" ")}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    const show = (v) => (v && String(v).trim() !== "" ? v : "N/A");

    const statusBadgeClasses = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "connected") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
        if (s === "unconnected") return "bg-rose-50 text-rose-700 ring-rose-200";
        if (s === "requested") return "bg-amber-50 text-amber-700 ring-amber-200";
        return "bg-slate-50 text-slate-700 ring-slate-200";
    };

    // --- Shared react-select style (44px controls) ---
    const selectStyles44 = {
        control: (p) => ({
            ...p,
            minHeight: 44,
            height: 44,
            borderColor: "#e2e8f0",
            boxShadow: "none",
            borderRadius: 12,
            "&:hover": { borderColor: "#cbd5e1" },
        }),
        valueContainer: (p) => ({ ...p, height: 44 }),
        input: (p) => ({ ...p, margin: 0, padding: 0 }),
        option: (p, s) => ({
            ...p,
            backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
            color: s.isSelected ? "white" : "#0f172a",
        }),
        menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
        menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    };

    const FilterPopover = ({ btnText, filter, setFilter, options }) => {
        const [open, setOpen] = useState(false);
        const [draftOp, setDraftOp] = useState(filter.op);
        const [draftVals, setDraftVals] = useState(filter.values || []);
        const ref = React.useRef(null);

        useEffect(() => {
            if (open) {
                setDraftOp(filter.op);
                setDraftVals(filter.values || []);
            }
        }, [open, filter.op, filter.values]);

        useEffect(() => {
            const onDoc = (e) => {
                if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
            };
            document.addEventListener("mousedown", onDoc);
            return () => document.removeEventListener("mousedown", onDoc);
        }, [open]);

        const disabled = draftOp === "has" || draftOp === "not_has";

        return (
            <div className="relative">
                <label className="block text-xs font-semibold text-slate-600 mb-1">AI position</label>
                <button
                    type="button"
                    className="h-11 inline-flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                    onClick={() => setOpen((o) => !o)}
                >
                    <span className="truncate">Filter: {btnText}</span>
                    <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                            {filter?.values?.length ? filter.values.length : "Any"}
                        </span>
                        <span className="text-slate-400" aria-hidden="true">
                            ▾
                        </span>
                    </span>
                </button>

                {open && (
                    <div
                        ref={ref}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-slate-200 shadow-xl p-4 z-20"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-extrabold text-slate-900">Filter by {btnText}</div>
                            <button
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                onClick={() => setOpen(false)}
                                type="button"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-3">
                            <Select
                                value={OP_OPTIONS.find((o) => o.value === draftOp)}
                                onChange={(op) => setDraftOp(op.value)}
                                options={OP_OPTIONS}
                                classNamePrefix="react-select"
                                placeholder="Equals"
                                styles={selectStyles44}
                            />
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isDisabled={disabled}
                                value={draftVals}
                                onChange={(vals) => setDraftVals(vals || [])}
                                options={options}
                                classNamePrefix="react-select"
                                placeholder="Select…"
                                isClearable={false}
                                styles={{
                                    ...selectStyles44,
                                    control: (p) => ({
                                        ...selectStyles44.control(p),
                                        opacity: disabled ? 0.65 : 1,
                                    }),
                                }}
                            />
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                                onClick={() => setDraftVals([])}
                                type="button"
                            >
                                Clear
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                                onClick={() => {
                                    setFilter({ op: draftOp, values: draftVals });
                                    setOpen(false);
                                }}
                                type="button"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- JSX ---
    return (
        <div className="min-h-screen bg-gray-100 px-3 sm:px-4 md:px-6 py-6">
            <div className="w-full max-w-full mx-auto">
                <div className="flex flex-col gap-5 sm:gap-6">
                    {/* Header */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                        Contact Management
                                    </h1>
                                    <p className="mt-1 text-sm text-slate-600">
                                        Manage contacts, apply filters, and take actions without leaving the table.
                                    </p>
                                </div>

                                <div className="inline-flex items-center gap-2">
                                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                        {hasFilters
                                            ? `Filtered: ${filteredContacts.length} / ${contacts.length}`
                                            : `Total: ${contacts.length}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Actions */}
                    {/* <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Create
                                </button>

                                <button
                                    onClick={handleEmailData}
                                    disabled={emailBtnDisabled || !filteredContacts.length}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    Email me data
                                </button>

                                <button
                                    onClick={handleAddToQueue}
                                    disabled={addToQueueBtnDisabled || !filteredContacts.length}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Add to connect queue
                                </button>

                                <button
                                    onClick={fetchContacts}
                                    disabled={isRefreshing}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="23 4 23 10 17 10" />
                                        <polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.13-3.36" />
                                        <path d="M20.49 15a9 9 0 0 1-14.13 3.36" />
                                    </svg>
                                    {isRefreshing ? "Refreshing…" : "Refresh"}
                                </button>

                                <button
                                    onClick={() => (window.location.href = "/contact-upload")}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    Contact upload
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 ml-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </section> */}

                    {/* Filters */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="flex flex-wrap items-end gap-2 sm:gap-3 lg:gap-4">
                                <div className="flex-[2] min-w-[260px] w-full">
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                                    <input
                                        type="text"
                                        placeholder="Search by name, company, position…"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm sm:text-base text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                                    />
                                </div>

                                <div className="w-72 sm:w-80">
                                    <FilterPopover
                                        btnText="AI Position"
                                        filter={aiPositionFilter}
                                        setFilter={setAiPositionFilter}
                                        options={aiPositionOptions}
                                    />
                                </div>

                                <div className="w-56 sm:w-64">
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Status</label>
                                    <Select
                                        value={statusFilter ? { value: statusFilter, label: statusFilter } : { value: "", label: "All statuses" }}
                                        onChange={(opt) => setStatusFilter(opt?.value || "")}
                                        options={[
                                            { value: "", label: "All statuses" },
                                            ...statusOptions.map((s) => ({ value: s, label: s })),
                                        ]}
                                        classNamePrefix="react-select"
                                        styles={selectStyles44}
                                    />
                                </div>
                            </div>

                            <div className="mt-4 text-sm text-slate-600">
                                {hasFilters
                                    ? `Showing ${filteredContacts.length} of ${contacts.length} contact${contacts.length === 1 ? "" : "s"}.`
                                    : `Total contacts: ${contacts.length}.`}
                            </div>
                        </div>
                    </section>

                    {/* Table */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200">
                                <table className="min-w-full table-auto divide-y divide-slate-200">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            {[
                                                ["Email", "email"],
                                                ["Name", "contact_name"],
                                                ["Profile", "profile_url"],
                                                ["Company", "company_name"],
                                                ["Website", "company_website"],
                                                ["AI Position", "ai_position"],
                                                ["Follower", "follower_count"],
                                                ["Created", "created_at"],
                                            ].map(([label, field]) => {
                                                const active = sortField === field;
                                                return (
                                                    <th
                                                        key={field}
                                                        onClick={() => handleSort(field)}
                                                        className={[
                                                            "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 cursor-pointer select-none hover:bg-slate-100",
                                                            active ? "bg-slate-100" : "",
                                                        ].join(" ")}
                                                    >
                                                        <span className="inline-flex items-center gap-1">
                                                            {label}
                                                            {active && <span className="text-slate-900">{sortOrder === "asc" ? "↑" : "↓"}</span>}
                                                        </span>
                                                    </th>
                                                );
                                            })}
                                            {/* <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                                                Actions
                                            </th> */}
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-200 bg-white text-sm text-slate-900">
                                        {displayedContacts.length > 0 ? (
                                            displayedContacts.map((c) => (
                                                <tr
                                                    key={c.contact_id}
                                                    className="group cursor-pointer hover:bg-slate-50"
                                                    onClick={() => {
                                                        setContactDetails(c);
                                                        setIsDetailsModalOpen(true);
                                                    }}
                                                >
                                                    <td className="px-4 py-4 font-medium max-w-[220px] truncate">
                                                        {(() => {
                                                            const primaryEmail =
                                                                (c.business_email && c.business_email !== "N/A" && c.business_email.trim()) ||
                                                                (c.email && c.email !== "N/A" && c.email.trim()) ||
                                                                null;

                                                            return primaryEmail ? (
                                                                <a
                                                                    href={`mailto:${primaryEmail}`}
                                                                    className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {primaryEmail}
                                                                </a>
                                                            ) : (
                                                                "N/A"
                                                            );
                                                        })()}
                                                    </td>

                                                    <td className="px-4 py-4 max-w-[200px] truncate">{c.contact_name}</td>

                                                    <td className="px-4 py-4 max-w-[240px] truncate">
                                                        {c.profile_url ? (
                                                            <a
                                                                href={c.profile_url.startsWith("http") ? c.profile_url : `https://${c.profile_url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {c.profile_url}
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-4 max-w-[220px] truncate text-slate-700">{show(c.company_name)}</td>

                                                    <td className="px-4 py-4 max-w-[240px] truncate">
                                                        {c.company_website && c.company_website !== "N/A" ? (
                                                            <a
                                                                href={
                                                                    c.company_website.startsWith("http")
                                                                        ? c.company_website
                                                                        : `https://${c.company_website}`
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {c.company_website}
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-4 max-w-[220px] truncate">
                                                        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 bg-slate-50 text-slate-700 ring-slate-200">
                                                            {show(c.ai_position)}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-4 whitespace-nowrap text-slate-700">{show(c.follower_count)}</td>

                                                    <td className="px-4 py-4 whitespace-nowrap text-slate-700">{formatDate(c.created_at)}</td>

                                                    {/* <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center justify-start gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setContactToUpdate(c);
                                                                    setIsUpdateModalOpen(true);
                                                                }}
                                                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                                title="Update Contact"
                                                                type="button"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-7.293 7.293A1 1 0 018.001 14H5a1 1 0 01-1-1v-3a1 1 0 01.293-.707l7.293-7.293zM14 6L11 9l-2-2 3-3 2 2z" />
                                                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                            </button>

                                                            <button
                                                                onClick={() => {
                                                                    setContactToDelete(c);
                                                                    setIsDeleteModalOpen(true);
                                                                }}
                                                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                                title="Delete Contact"
                                                                type="button"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M9 2a1 1 0 00-1 1v1H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-4V3a1 1 0 00-1-1H9zm1 2H9v1h2V4zm0 2H9v6h2V6z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M10 2a1 1 0 011 1v1h4a1 1 0 010 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6a1 1 0 010-2h4V3a1 1 0 011-1z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </button>

                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAddSingleToQueue(c.contact_id, c.status);
                                                                }}
                                                                disabled={addToQueueBtnDisabled}
                                                                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                                title="Add to Connect Queue"
                                                                type="button"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </td> */}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={9} className="py-10 text-center text-slate-500">
                                                    No contacts found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden space-y-3">
                                {displayedContacts.length > 0 ? (
                                    displayedContacts.map((c) => (
                                        <div
                                            key={c.contact_id}
                                            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50 cursor-pointer"
                                            onClick={() => {
                                                setContactDetails(c);
                                                setIsDetailsModalOpen(true);
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm text-slate-700">
                                                        <span className="font-semibold text-slate-900">Name:</span> {show(c.contact_name)}
                                                    </p>
                                                    <p className="mt-1 text-sm text-slate-700">
                                                        <span className="font-semibold text-slate-900">Company:</span> {show(c.company_name)}
                                                    </p>
                                                </div>

                                                <span
                                                    className={[
                                                        "shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                                                        statusBadgeClasses(c.status),
                                                    ].join(" ")}
                                                >
                                                    {show(c.status)}
                                                </span>
                                            </div>

                                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                                                <div className="col-span-2">
                                                    <span className="font-semibold text-slate-900">AI Position:</span>{" "}
                                                    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 bg-slate-50 text-slate-700 ring-slate-200">
                                                        {show(c.ai_position)}
                                                    </span>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="font-semibold text-slate-900">Created:</span> {formatDate(c.created_at)}
                                                </div>
                                            </div>

                                            {/* <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    onClick={() => {
                                                        setContactToUpdate(c);
                                                        setIsUpdateModalOpen(true);
                                                    }}
                                                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                    title="Update Contact"
                                                    type="button"
                                                >
                                                    Update
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setContactToDelete(c);
                                                        setIsDeleteModalOpen(true);
                                                    }}
                                                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                    title="Delete Contact"
                                                    type="button"
                                                >
                                                    Delete
                                                </button>

                                                <button
                                                    onClick={() => handleAddSingleToQueue(c.contact_id, c.status)}
                                                    disabled={addToQueueBtnDisabled}
                                                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                    title="Add to Connect Queue"
                                                    type="button"
                                                >
                                                    Add to queue
                                                </button>
                                            </div> */}
                                        </div>
                                    ))
                                ) : (
                                    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
                                        No contacts found.
                                    </div>
                                )}
                            </div>

                            {/* Pagination */}
                            {pageCount > 1 && (
                                <div className="mt-6">
                                    <ReactPaginate
                                        previousLabel={
                                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                                ← Previous
                                            </span>
                                        }
                                        nextLabel={
                                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                                Next →
                                            </span>
                                        }
                                        pageCount={pageCount}
                                        onPageChange={({ selected }) => setCurrentPage(selected)}
                                        containerClassName="flex flex-wrap items-center justify-center gap-2"
                                        pageClassName="block"
                                        pageLinkClassName="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
                                        activeLinkClassName="!bg-slate-900 !text-white !border-slate-900"
                                        disabledClassName="opacity-50 cursor-not-allowed"
                                        breakClassName="px-2 py-2 text-slate-400"
                                        renderOnZeroPageCount={null}
                                    />
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Modals */}
                    {[
                        {
                            isOpen: isCreateModalOpen,
                            onRequestClose: () => setIsCreateModalOpen(false),
                            title: "Create New Contact",
                            content: renderFields(newContact, false, false),
                            onSave: handleCreateContact,
                            saveLabel: "Create",
                        },
                        {
                            isOpen: isUpdateModalOpen,
                            onRequestClose: () => setIsUpdateModalOpen(false),
                            title: "Update Contact",
                            content: renderFields(contactToUpdate, true, false),
                            onSave: handleUpdateContact,
                            saveLabel: "Update",
                        },
                        {
                            isOpen: isDetailsModalOpen,
                            onRequestClose: () => setIsDetailsModalOpen(false),
                            title: "Contact Details",
                            content: renderFields(contactDetails, false, true),
                            saveLabel: "Close",
                            isReadOnlyClose: true,
                        },
                    ].map((modal, i) => (
                        <ReactModal
                            key={i}
                            isOpen={modal.isOpen}
                            onRequestClose={modal.onRequestClose}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(44rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">{modal.title}</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        {modal.isReadOnlyClose ? "Read-only view." : "Edit fields and save changes."}
                                    </p>
                                </div>
                                <button
                                    onClick={modal.onRequestClose}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    ✕
                                </button>
                            </div>

                            {modal.content}

                            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={modal.onRequestClose}
                                    className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                    type="button"
                                >
                                    {modal.isReadOnlyClose ? modal.saveLabel : "Cancel"}
                                </button>

                                {!modal.isReadOnlyClose && (
                                    <button
                                        onClick={modal.onSave}
                                        className={[
                                            "py-2 px-4 rounded-xl font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2",
                                            modal.saveLabel === "Create"
                                                ? "bg-slate-900 hover:bg-slate-800 focus:ring-slate-400/60"
                                                : "bg-amber-600 hover:bg-amber-700 focus:ring-amber-300",
                                        ].join(" ")}
                                        type="button"
                                    >
                                        {modal.saveLabel}
                                    </button>
                                )}
                            </div>
                        </ReactModal>
                    ))}

                    {/* Delete Confirmation Modal */}
                    <ReactModal
                        isOpen={isDeleteModalOpen}
                        onRequestClose={() => setIsDeleteModalOpen(false)}
                        className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(28rem,calc(100vw-2rem))] outline-none border border-slate-200"
                        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                    >
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Confirm Deletion</h2>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        <p className="text-slate-700 mb-5 text-sm sm:text-base">
                            Are you sure you want to delete the contact for{" "}
                            <span className="font-semibold text-slate-900">{contactToDelete?.contact_name}</span>? This action
                            cannot be undone.
                        </p>

                        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="py-2 px-4 rounded-xl bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteContact}
                                className="py-2 px-4 rounded-xl bg-red-600 text-white font-semibold shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition"
                                type="button"
                            >
                                Delete
                            </button>
                        </div>
                    </ReactModal>
                </div>
            </div>
        </div>
    );
};

export default ContactManagement;
